/**
 * What grows where.
 *
 * Two tables per biome. The **forest mix** says what a mapped forest polygon
 * is made of — spruce in the taiga, palms and jungle giants on the equator,
 * olives and cypress around the Mediterranean. The **open-ground mix** says
 * what stands in the land between: nothing much in a temperate field, an acacia
 * every hundred metres on the savanna, rocks and dry scrub in a desert. Both
 * blend by biome weight, so a tile at the edge of two biomes gets both floras
 * in proportion.
 *
 * Species are small integers so the worker can hand them over in a typed
 * array. The renderer owns what each one looks like.
 */

import type { Biome, BiomeWeights } from './biome.js';
import { BIOMES } from './biome.js';
import type { Climate } from './climate.js';

export const SPECIES = [
  'pine', 'spruce', 'snowySpruce', 'birch', 'broadleaf', 'jungle', 'palm', 'acacia',
  'cactus', 'olive', 'cypress', 'shrub', 'dryShrub', 'rock', 'tuft', 'dryTuft', 'stump',
  // Street furniture and fixtures: placed from the map, never by biome.
  'bench', 'bin', 'postbox', 'lamp', 'shelter', 'fountain', 'memorial', 'tower',
  'gravestone', 'swing', 'slide', 'goal', 'tennisNet',
  // Seasonal states of the deciduous trees, and a snow-laden pine.
  'broadleafAutumn', 'broadleafBare', 'birchAutumn', 'birchBare', 'pineSnowy',
  // Garden boundaries, in pieces along the estimated plots: a low hedge, a
  // picket fence, a post-and-rail fence. See `build/plots.ts`.
  'hedge', 'picket', 'rail',
  // Parked vehicles: in front of garages, on driveways, in car parks. Drawn
  // like props, and any of them can be taken for a drive. See `build/parking.ts`.
  'carHatch', 'carSedan', 'carEstate', 'carSuv', 'carVan', 'carPickup', 'parkedMoto',
  // Trucks, parked where goods are loaded: a box truck, and a tractor with its trailer.
  'truckBox', 'truckSemi',
  // A grown Scots pine (Swedish "tall"): the long bare trunk, orange above, and
  // a flat crown at the top, for pines the data says are tall (`grown`); and
  // the same with snow.
  'tallPine', 'tallPineSnowy',
  // Buses: Västtrafik's area, express and metro designs, and a plain one in
  // an operator's colour. They run their lines in traffic (`core/transit`).
  'busArea', 'busExpress', 'busMetro', 'busCity',
  // An e-scooter, parked outside garages and in the odd bay: see `three/Scooter.ts`.
  'parkedScooter',
] as const;

export type Species = (typeof SPECIES)[number];
export const SPECIES_ID: Record<Species, number> = Object.fromEntries(
  SPECIES.map((s, i) => [s, i]),
) as Record<Species, number>;

/** Species that are trees, as opposed to ground props. */
export const TREES: ReadonlySet<Species> = new Set([
  'pine', 'spruce', 'snowySpruce', 'birch', 'broadleaf', 'jungle', 'palm', 'acacia', 'olive', 'cypress',
  'broadleafAutumn', 'broadleafBare', 'birchAutumn', 'birchBare', 'pineSnowy', 'tallPine', 'tallPineSnowy',
]);

/** Man-made props: exempt from the tree line, and never picked by a biome mix. */
export const PROPS: ReadonlySet<Species> = new Set([
  'bench', 'bin', 'postbox', 'lamp', 'shelter', 'fountain', 'memorial', 'tower',
  'gravestone', 'swing', 'slide', 'goal', 'tennisNet',
  'hedge', 'picket', 'rail',
  'carHatch', 'carSedan', 'carEstate', 'carSuv', 'carVan', 'carPickup', 'parkedMoto', 'truckBox', 'truckSemi',
  'busArea', 'busExpress', 'busMetro', 'busCity', 'parkedScooter',
]);

/** The parked vehicles among the props. */
export const VEHICLES: ReadonlySet<Species> = new Set([
  'carHatch', 'carSedan', 'carEstate', 'carSuv', 'carVan', 'carPickup', 'parkedMoto', 'truckBox', 'truckSemi',
  'busArea', 'busExpress', 'busMetro', 'busCity', 'parkedScooter',
]);

/**
 * Pieces laid end to end along a line: their instance scale stretches them
 * along their length only, so a run fits its boundary exactly.
 */
export const STRETCHED: ReadonlySet<Species> = new Set(['hedge', 'picket', 'rail']);

type Mix = Partial<Record<Species, number>>;

/**
 * Inside mapped forest. Every mix keeps a share of undergrowth — shrubs,
 * rocks, tufts — so a wood never reads as a plantation of one model.
 */
const FOREST: Record<Biome, Mix> = {
  glacier: { rock: 1 },
  tundra: { snowySpruce: 0.35, dryShrub: 0.25, rock: 0.25, dryTuft: 0.15 },
  boreal: { spruce: 0.5, pine: 0.16, birch: 0.16, shrub: 0.08, rock: 0.05, stump: 0.05 },
  temperate: { pine: 0.38, broadleaf: 0.26, birch: 0.08, shrub: 0.13, tuft: 0.09, rock: 0.04, stump: 0.02 },
  mediterranean: { olive: 0.3, cypress: 0.18, pine: 0.22, broadleaf: 0.08, dryShrub: 0.14, rock: 0.08 },
  steppe: { pine: 0.3, broadleaf: 0.25, dryShrub: 0.25, dryTuft: 0.12, rock: 0.08 },
  desert: { palm: 0.6, dryShrub: 0.25, rock: 0.15 },
  savanna: { acacia: 0.45, broadleaf: 0.15, palm: 0.1, dryShrub: 0.18, dryTuft: 0.12 },
  rainforest: { jungle: 0.55, palm: 0.18, broadleaf: 0.12, shrub: 0.15 },
};

/** Open ground outside forest: the props and, where it fits, lone trees. */
const OPEN: Record<Biome, Mix> = {
  glacier: { rock: 1 },
  tundra: { rock: 0.55, dryTuft: 0.3, dryShrub: 0.15 },
  boreal: { rock: 0.4, shrub: 0.3, tuft: 0.3 },
  temperate: { tuft: 0.6, shrub: 0.3, rock: 0.1 },
  mediterranean: { olive: 0.22, cypress: 0.08, dryShrub: 0.4, rock: 0.2, dryTuft: 0.1 },
  steppe: { dryTuft: 0.6, dryShrub: 0.3, rock: 0.1 },
  desert: { rock: 0.55, dryShrub: 0.33, cactus: 0.12 },
  savanna: { acacia: 0.18, dryShrub: 0.3, dryTuft: 0.5, rock: 0.02 },
  rainforest: { shrub: 0.6, palm: 0.25, tuft: 0.15 },
};

/**
 * Props per square metre of open ground. Kept at zero-ish where the reference
 * look is a clean lawn, and highest where the open land *is* the landscape.
 */
const OPEN_DENSITY: Record<Biome, number> = {
  glacier: 0.00002,
  tundra: 0.0005,
  boreal: 0.00008,
  temperate: 0.00006,
  mediterranean: 0.0007,
  steppe: 0.0008,
  desert: 0.00035,
  savanna: 0.0011,
  rainforest: 0.0002,
};

function blendMix(weights: BiomeWeights, table: Record<Biome, Mix>, climate: Climate): Array<[number, number]> {
  const totals = new Float64Array(SPECIES.length);
  for (const b of BIOMES) {
    const w = weights[b];
    if (w <= 0) continue;
    for (const [species, share] of Object.entries(table[b]) as Array<[Species, number]>) {
      totals[SPECIES_ID[species]] += w * share;
    }
  }
  // Cacti are American. A Saharan dune field gets dry scrub instead.
  if (!climate.americas) {
    totals[SPECIES_ID.dryShrub] += totals[SPECIES_ID.cactus];
    totals[SPECIES_ID.cactus] = 0;
  }
  const out: Array<[number, number]> = [];
  let sum = 0;
  totals.forEach((v) => { sum += v; });
  totals.forEach((v, id) => { if (v > 0) out.push([id, v / sum]); });
  return out;
}

/** A sampler that draws species ids in the blended proportions. */
export interface FloraMix {
  pick(u: number): number;
}

function sampler(entries: Array<[number, number]>): FloraMix {
  const cumulative: number[] = [];
  let run = 0;
  for (const [, p] of entries) cumulative.push((run += p));
  return {
    pick(u: number): number {
      for (let i = 0; i < cumulative.length; i++) if (u <= cumulative[i]) return entries[i][0];
      return entries[entries.length - 1]?.[0] ?? SPECIES_ID.rock;
    },
  };
}

export function forestMix(weights: BiomeWeights, climate: Climate): FloraMix {
  return sampler(blendMix(weights, FOREST, climate));
}

export function openMix(weights: BiomeWeights, climate: Climate): FloraMix {
  return sampler(blendMix(weights, OPEN, climate));
}

export function openDensity(weights: BiomeWeights): number {
  let d = 0;
  for (const b of BIOMES) d += weights[b] * OPEN_DENSITY[b];
  return d;
}

/** How tall a pine is before it has lost its lower branches and stands as a grown pine, metres. */
export const GROWN_PINE = 14;

/**
 * The species as the tree's measured height shows it: a pine as tall as
 * `GROWN_PINE` is a grown one, its crown high on a bare trunk; a young pine
 * is still a cone of branches to the ground.
 */
export function grown(species: Species, height: number): Species {
  return species === 'pine' && height >= GROWN_PINE ? 'tallPine' : species;
}

/**
 * Dress a tree for the season.
 *
 * Deciduous trees go to their autumn colours and then bare, each on its own
 * schedule — `u`, a per-tree random in [0,1), decides which trees turn first,
 * which is how a real wood colours: in patches, not all at once. Conifers
 * carry snow when snow lies.
 */
export function adaptToSeason(
  species: number,
  season: { leaves: number; autumn: number },
  snow: number,
  u: number,
): number {
  const name = SPECIES[species];
  if (name === 'broadleaf' || name === 'birch') {
    if (u > season.leaves) return SPECIES_ID[name === 'birch' ? 'birchBare' : 'broadleafBare'];
    if (u < season.autumn) return SPECIES_ID[name === 'birch' ? 'birchAutumn' : 'broadleafAutumn'];
    return species;
  }
  if (snow > 0.45) {
    if (name === 'pine') return SPECIES_ID.pineSnowy;
    if (name === 'tallPine') return SPECIES_ID.tallPineSnowy;
    if (name === 'spruce') return SPECIES_ID.snowySpruce;
  }
  return species;
}

/**
 * Adjust a species for the temperature where it actually stands.
 *
 * The worker picks species from the tile's climate at sea level, because it has
 * no terrain; placement knows each tree's height and calls this. Above the tree
 * line trees become snow-laden spruce, and above that, bare rock. Returns -1
 * when nothing grows there at all.
 */
export function adaptToTemperature(species: number, temperature: number): number {
  if (PROPS.has(SPECIES[species])) return species;
  if (!TREES.has(SPECIES[species])) {
    return temperature < -9 && species !== SPECIES_ID.rock ? -1 : species;
  }
  if (temperature < -4) return -1;
  if (temperature < 0.5) return SPECIES_ID.snowySpruce;
  if (temperature < 2.5 && species !== SPECIES_ID.birch) return SPECIES_ID.spruce;
  return species;
}
